import { useState, useEffect } from 'react'
import * as z from 'zod'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CheckCircle2, XCircle } from 'lucide-react'
import { sendOwnerVerificationLinkFromEmail } from '@/lib/hooks/mail'
import { FeedbackDialog } from './feedback-dialog'
import WhatsAppInput from './input/whatsApp-mask-input'
import { getCleanDomain } from '@/utils/getCleanDomain'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import CPFInput from './input/cpf-mask-input'
import { isOwnerEmailVerified, persistOwnerEmailVerified } from '@/utils/owner-email-verification'

interface VerificacaoUsuarioProps {
  onSuccess: (data: { name: string; cpf: string; whatsapp: string; email: string }) => void
  expectedCpf?: string
}

const WHATSAPP_VERIFICATION_ENABLED = false

const schema = z.object({
  name: z.string().min(1, { message: 'Nome é obrigatório' }),
  whatsapp: z.string().min(1, { message: 'WhatsApp é obrigatório' }),
  cpf: z.string().min(1, { message: 'CPF é obrigatório' }),
  whatsappCode: WHATSAPP_VERIFICATION_ENABLED
    ? z.string().min(1, { message: 'Código do WhatsApp é obrigatório' })
    : z.string().optional(),
  email: z.string().email({ message: 'E-mail inválido' }),
  emailCode: z.string().optional()
})

type FormData = z.infer<typeof schema>

const normalizeCpf = (value?: string | null) => String(value || '').replace(/\D/g, '').trim()

export default function CheckContacts({ onSuccess, expectedCpf }: VerificacaoUsuarioProps) {
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      cpf: '',
      whatsapp: '',
      whatsappCode: '',
      email: '',
      emailCode: ''
    }
  })
  const [whatsappVerified, setWhatsappVerified] = useState(false)
  const [emailVerified, setEmailVerified] = useState(false)
  const [emailCodeSent, setEmailCodeSent] = useState(false)
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSendingEmail, setIsSendingEmail] = useState(false)

  const [message, setMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)

  const { mutate: sendEmail } = sendOwnerVerificationLinkFromEmail()
  useEffect(() => {
    if (WHATSAPP_VERIFICATION_ENABLED) {
      setWhatsappVerified(false)
      return
    }

    const hasWhatsapp = Boolean(form.getValues('whatsapp')?.trim())
    setWhatsappVerified(hasWhatsapp)
  }, [form.watch('whatsapp')])

  useEffect(() => {
    setEmailVerified(false)
    setEmailCodeSent(false)
  }, [form.watch('email')])

  useEffect(() => {
    const email = form.getValues('email')?.trim().toLowerCase()
    if (!email) return

    const syncEmailVerification = () => {
      if (isOwnerEmailVerified(email)) {
        setEmailVerified(true)
        setEmailCodeSent(true)
        setAlert({ type: 'success', message: 'E-mail verificado com sucesso!' })
      }
    }

    syncEmailVerification()
    window.addEventListener('storage', syncEmailVerification)
    const interval = window.setInterval(syncEmailVerification, 1000)

    return () => {
      window.removeEventListener('storage', syncEmailVerification)
      window.clearInterval(interval)
    }
  }, [form, form.watch('email')])

  const sendEmailCode = () => {
    if (isSendingEmail) return

    // deve ser tratado na api e enviado para o front caso um teste local apenas :(
    // se o codigo eh gerado no front qualquer um pode gerar ele e enviar
    // para o whatsapp ou email

    const domain = getCleanDomain()
    const empresa = domain.split('.')
    console.log('empresa', empresa)
    const { email, cpf } = form.getValues()
    const document = String(cpf || '').trim()
    const normalizedExpectedCpf = normalizeCpf(expectedCpf)

    if (normalizedExpectedCpf && normalizeCpf(document) !== normalizedExpectedCpf) {
      setAlert({
        type: 'error',
        message: 'O CPF informado deve ser o mesmo CPF do proprietário do cliente.'
      })
      return
    }

    if (!document) {
      setAlert({
        type: 'error',
        message: 'Informe o CPF antes de enviar o link de confirmação.'
      })
      return
    }

    setIsSendingEmail(true)
    sendEmail(
      { empresa: empresa[0], to: email, document },
      {
        onSuccess: response => {
          const alreadyVerified = Boolean(response?.data?.alreadyVerified)

          setEmailCodeSent(true)
          setIsSendingEmail(false)

          if (alreadyVerified) {
            persistOwnerEmailVerified(email)
            setEmailVerified(true)
          }

          setMessage({
            type: 'success',
            text: alreadyVerified
              ? `O e-mail ${email} já estava verificado.`
              : `Link de confirmação enviado para o e-mail ${email}!`
          })
          setIsDialogOpen(true)
          setAlert({
            type: 'success',
            message: alreadyVerified ? `O e-mail ${email} já está verificado.` : `Link de confirmação enviado para ${email}.`
          })
        },

        onError: (error: { message: any }) => {
          setMessage({
            type: 'error',
            text: `Erro ao enviar o código para E-mail ${email}: ${error.message}`
          })
          setIsDialogOpen(true)
          setEmailCodeSent(false)
          setIsSendingEmail(false)
        }
      }
    )
  }
  const isPrivilegedUser = false
  const createUser = (data: FormData) => {
    const normalizedExpectedCpf = normalizeCpf(expectedCpf)
    const normalizedCpf = normalizeCpf(data.cpf)

    if (normalizedExpectedCpf && normalizedCpf !== normalizedExpectedCpf) {
      setAlert({
        type: 'error',
        message: 'O CPF informado deve ser o mesmo CPF do proprietário do cliente.'
      })
      return
    }

    const hasWhatsappVerification = WHATSAPP_VERIFICATION_ENABLED ? whatsappVerified : Boolean(data.whatsapp?.trim())

    if (!hasWhatsappVerification || !emailVerified) {
      if (!isPrivilegedUser) {
        setAlert({
          type: 'error',
          message: 'Informe o WhatsApp e verifique o E-mail antes de salvar.'
        })
        return
      }
    }

    setAlert({
      type: 'success',
      message: 'Proprietário verificado! Continue o cadastro'
    })
    onSuccess(data)
  }

  return (
    <div className="w-full">
      <div className="py-2  mb-2 border-b">
        <h2 className="font-bold text-base">Verificação de Proprietário</h2>
        <p className="text-sm">Preencha os dados, informe o WhatsApp e confirme o e-mail para criar uma conta</p>
      </div>
      <div className="space-y-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(createUser)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Nome Completo do proprietário</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome completo" className="flex-grow bg-input" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cpf"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>CPF</FormLabel>
                  <FormControl>
                    <CPFInput
                      id="cpf"
                      placeholder="Digite o CPF"
                      value={field.value}
                      onCPFChange={(value: string) => form.setValue('cpf', value, { shouldValidate: true })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="whatsapp"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>WhatsApp</FormLabel>
                  <FormControl>
                    <WhatsAppInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Verificação por código do WhatsApp desativada nesta etapa.
                O campo permanece apenas para coleta do número. */}

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        type="email"
                        placeholder="Seu endereço de e-mail"
                        className="flex-grow bg-input"
                        {...field}
                      />
                      <Button
                        onClick={sendEmailCode}
                        className="w-full sm:w-auto bg-primary hover:bg-primary-hover text-primary-foreground"
                        disabled={isSendingEmail || emailCodeSent || !form.watch('email') || !form.watch('cpf')}
                        type="button"
                      >
                        Enviar Código
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="mt-2 rounded-md border border-dashed p-3 text-sm text-muted-foreground">
              {emailVerified
                ? 'E-mail confirmado.'
                : 'Ao clicar em "Enviar Código", você receberá um link. Abra esse link para confirmar que o e-mail é seu.'}
            </div>

            <FeedbackDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} message={message} />

            {alert && (
              <div
                className={`flex items-start space-x-2 rounded-md border p-4 my-4 ${
                  alert.type === 'success'
                    ? 'bg-green-100 border-green-200 text-green-800'
                    : 'bg-red-100 border-red-200 text-red-800'
                }`}
              >
                {alert.type === 'success' ? (
                  <CheckCircle2 className="mt-1 h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="mt-1 h-5 w-5 text-red-500" />
                )}
                <div>
                  <p className="font-medium">{alert.type === 'success' ? 'Sucesso' : 'Erro'}</p>
                  <p className="text-sm">{alert.message}</p>
                </div>
              </div>
            )}

            <Button
              className="mt-3 w-full bg-primary hover:bg-primary-hover text-primary-foreground"
              disabled={(!whatsappVerified || !emailVerified) && !isPrivilegedUser}
              type="submit"
            >
              Salvar
            </Button>
          </form>
        </Form>
      </div>
    </div>
  )
}
